/**
 * Node image (InstalledBaseNode.image / imageId) — pure form-value logic.
 *
 * The backend models the node image as a *role* of one of the node's files:
 * `imageId` in the create/update input is the **file-service UUID** (the same
 * value `addFilesToInstalledBaseNode` takes as `fileServiceUuid`). Semantics:
 *   - UUID          → file is attached as node file if needed and linked as image
 *   - explicit null → image unlinked, the file itself stays
 *   - key absent    → image unchanged
 *
 * The TDA forms seed `imageId` with `attr.value || ''`, and the payload builder
 * maps `'' → null`. Without the "unchanged → omit" step below every save would
 * therefore unlink the image. Uploading is injected so this file stays testable.
 */

/** TDA index of the node image attribute (also the input field name). */
export const NODE_IMAGE_FIELD = 'imageId';

/**
 * Formik-only key holding a freshly picked `File`. Never sent to the backend.
 * Kept separate from `imageId` because the generated Yup schema validates
 * `imageId` as a nullable string — a `File` there would block submission.
 */
export const NODE_IMAGE_FILE_FIELD = '_imageFile';

/**
 * Sentinel inputType routing the TDA to the app's NodeImageInput renderer. The
 * shared TDAForm resolves its built-in `image` branch *before* consulting
 * `customInputRenderers`, so the attribute is re-tagged (see withNodeImageInputType).
 */
export const NODE_IMAGE_INPUT_TYPE = 'nodeImage';

/** Every active query that renders or edits a node image — refetched after a change. */
export const NODE_IMAGE_REFETCH_QUERIES = [
    'AssetNameplateBasicInfo',
    'SystemNameplateInfo',
    'InstalledBaseNodeDetail',
    'StarredInstalledBaseNodeList',
    'InstalledBaseNodeFiles',
    'AssetFiles',
    'SystemFiles',
    'InstalledBaseNodeTdas',
    'InstalledBaseNodeInformation',
];

export type NodeImageChange =
    | { kind: 'unchanged' }
    | { kind: 'remove' }
    | { kind: 'upload'; file: File }
    | { kind: 'set'; fileId: string };

const normalizeImageId = (value: unknown): string | null =>
    typeof value === 'string' && value !== '' ? value : null;

const isFile = (value: unknown): value is File =>
    typeof File !== 'undefined' && value instanceof File;

/**
 * Derives what the user did to the node image from the submitted form values.
 * `initialImageId` is the value the form started with ('' / null / UUID).
 */
export const getNodeImageChange = (
    formValues: Record<string, any>,
    initialImageId: string | null | undefined
): NodeImageChange => {
    const picked = formValues?.[NODE_IMAGE_FILE_FIELD];
    if (isFile(picked)) return { kind: 'upload', file: picked };

    const current = normalizeImageId(formValues?.[NODE_IMAGE_FIELD]);
    const initial = normalizeImageId(initialImageId);
    if (current === initial) return { kind: 'unchanged' };
    if (current === null) return { kind: 'remove' };
    return { kind: 'set', fileId: current };
};

/**
 * Returns a copy of `formValues` with the Formik-only file key removed and
 * `imageId` set to what the backend should receive: omitted when unchanged,
 * `null` to unlink, or the (uploaded) file-service UUID.
 */
export const applyNodeImageChange = (
    formValues: Record<string, any>,
    change: NodeImageChange,
    uploadedFileId?: string
): Record<string, any> => {
    const { [NODE_IMAGE_FILE_FIELD]: _pickedFile, ...rest } = formValues ?? {};
    switch (change.kind) {
        case 'unchanged': {
            const { [NODE_IMAGE_FIELD]: _unchanged, ...withoutImage } = rest;
            return withoutImage;
        }
        case 'remove':
            return { ...rest, [NODE_IMAGE_FIELD]: null };
        case 'set':
            return { ...rest, [NODE_IMAGE_FIELD]: change.fileId };
        case 'upload':
            if (!uploadedFileId) {
                throw new Error('applyNodeImageChange: upload change requires the uploaded file id');
            }
            return { ...rest, [NODE_IMAGE_FIELD]: uploadedFileId };
        default:
            return rest;
    }
};

/**
 * Uploads a newly picked image (if any) and returns the form values ready for
 * `separateInstalledBaseNode(Create)Attributes`. Rejects when the upload fails.
 */
export const prepareNodeImageFormValues = async (
    formValues: Record<string, any>,
    initialImageId: string | null | undefined,
    uploadFile: (file: File) => Promise<string>
): Promise<Record<string, any>> => {
    const change = getNodeImageChange(formValues, initialImageId);
    if (change.kind === 'upload') {
        const fileId = await uploadFile(change.file);
        return applyNodeImageChange(formValues, change, fileId);
    }
    return applyNodeImageChange(formValues, change);
};
